/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2022, Zumper
 * @description Nav component for use in maplist
 */

import { getAIAssistantSearchFilters, useZoeMapListVariation } from '@zcode-ui/filters'
import { Search } from '@zcode-ui/icons'
import { Button } from '@zcode-ui/shadz-ui'
import { ZoeHeaderPopover } from '@zcode-ui/web-zumper-features'
import { selectEntity } from '@zcode-ui/web-zumper-global-state'
import {
  type AutocompleteApiResult,
  type AutocompleteDetailLocationResult,
  AutocompleteResult,
} from '@zcode/bizos'
import { type EVENT_NAMES, PAGE_NAMES } from '@zcode/mixpanel'
import { voidEvent } from '@zcode/nr-utils'
import type { Origin } from '@zcode/nr-utils'
import { SharedAutocomplete } from '@zcode/nr/es/shared/components/Autocomplete/SharedAutocomplete'
import type { Filters } from '@zcode/state-type'
import type { Dispatch, GetState } from '@zcode/state-type'
import React, { useRef } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { NAV_SELECTIONS } from '../../../constants/index'
import { selectFilters } from '../../../module/selectors'
import type { DispatchMixpanelNavClick } from '../../../types'
import { getSelectedLocationText } from '../../HeaderMobileStr'
import theme from './autocomplete.module.scss'

export interface DesktopSingleNavInputProps {
  detailCityAndState?: string
  detailLocationObj?: AutocompleteDetailLocationResult
  dispatchMixpanelAutocompleteResultClick: ({
    result,
    index,
  }: {
    result: AutocompleteApiResult
    index: number
  }) => void
  dispatchMixpanelCompleteSearch: (leaseLength: NAV_SELECTIONS) => void
  dispatchMixpanelNavClick?: DispatchMixpanelNavClick
  getCurrentLocationName: (result: {
    lat?: number | null
    lng?: number | null
  }) => Promise<any>
  isCurrentSearchView?: boolean
  isDetailView?: boolean
  locationName?: string
  mapListNavTestId?: string
  mixpanelEvent?: (
    event: (typeof EVENT_NAMES)[keyof typeof EVENT_NAMES],
    origin: Origin | undefined,
    extraPayload?: object
  ) => (dispatch: Dispatch, getState: GetState) => void
  onSelectAutocompleteResult: (result: AutocompleteResult, filters?: Filters) => void
}

export const DesktopSingleNavInput = ({
  detailCityAndState,
  detailLocationObj,
  dispatchMixpanelAutocompleteResultClick,
  dispatchMixpanelCompleteSearch,
  dispatchMixpanelNavClick,
  getCurrentLocationName,
  isCurrentSearchView,
  isDetailView,
  locationName: locationNameProp,
  mapListNavTestId,
  mixpanelEvent,
  onSelectAutocompleteResult,
}: DesktopSingleNavInputProps) => {
  const navRef = useRef(null)
  const filters = useSelector(selectFilters)
  const entity = useSelector(selectEntity)
  const isShortTerm = !!filters.shortTerm
  const history = useHistory()
  const variation = useZoeMapListVariation(isCurrentSearchView)
  let locationName = isDetailView ? detailCityAndState : locationNameProp

  const onSelectLocation = () => {
    dispatchMixpanelNavClick?.({
      sectionTarget: 'Location',
      section: 'Global Search Bar',
    })
  }

  const onClickAutocompleteResult = async (result: AutocompleteResult) => {
    let locationString: string = getSelectedLocationText(result)

    if (result.type === 'building') {
      onSelectAutocompleteResult(result, filters)
      return
    }
    if (result.type === 'current_position') {
      const { lat, lng } = result
      const city: { name?: string; state?: string } = await getCurrentLocationName({ lat, lng })
      locationString = getSelectedLocationText(
        new AutocompleteResult({ ...result._data, city: city?.name })
      )
    }
    locationName = locationString

    onSelectAutocompleteResult(result, filters)
    onSelectLocation?.()
  }

  const cityUrl = entity?.cityUrl || '/'

  const handleSearch = (
    e: React.MouseEvent<HTMLButtonElement>,
    hoveredIndex: number,
    searchTerm: string
  ) => {
    dispatchMixpanelCompleteSearch(
      isShortTerm ? NAV_SELECTIONS.SHORT_TERM : NAV_SELECTIONS.LONG_TERM
    )

    if (isCurrentSearchView) {
      return
    }

    if ((detailLocationObj || cityUrl) && !searchTerm && hoveredIndex < 0) {
      voidEvent(e)

      if (detailLocationObj) {
        onSelectAutocompleteResult(detailLocationObj, filters)
        return
      }

      if (cityUrl) {
        history.push(cityUrl)
      }
    }
  }

  const pageName = isDetailView ? PAGE_NAMES.DETAIL : PAGE_NAMES.SEARCH_MAP
  const placeholder = isCurrentSearchView ? 'Where to?' : 'Where do you want to stay?'

  return (
    <div
      data-testid={mapListNavTestId}
      ref={navRef}
      className="flex justify-center relative h-full w-full"
    >
      <div className="flex flex-col mr-[56px] mt-[11px] w-[392px] [&_form]:relative [&_form]:max-w-[392px] [&_form_input[type=search]]:h-12 [&_form_input[type=search]]:py-3 [&_form_input[type=search]]:px-6">
        {variation === 'SHOW_ZOE' || variation === 'SHOW_ZOE_AUTOCOMPLETE_DEFAULT' ? (
          <div className="flex gap-2">
            <ZoeHeaderPopover
              dispatchMixpanelAutocompleteResultClick={dispatchMixpanelAutocompleteResultClick}
              getAIAssistantSearchFilters={getAIAssistantSearchFilters}
              handleSearchButtonClick={handleSearch}
              mixpanelEvent={mixpanelEvent}
              onSelectAutocompleteResult={onClickAutocompleteResult}
              placeholder={locationName || placeholder}
              variation={variation}
            />
            <Button className="h-12 w-12" variant="secondary">
              <Search fill="#fff" />
            </Button>
          </div>
        ) : (
          <SharedAutocomplete
            dispatchMixpanelAutocompleteResultClick={dispatchMixpanelAutocompleteResultClick}
            handleSearchButtonClick={handleSearch}
            isMapListNav
            isShortTerm={isShortTerm}
            onSelectAutocompleteResult={onClickAutocompleteResult}
            origin={isShortTerm ? 'short-term' : 'Long-term'}
            pageName={pageName}
            placeholder={locationName || placeholder}
            theme={theme}
          />
        )}
      </div>
    </div>
  )
}
